// ---------- ASCII alphabet, standard ramp + degraded blocks ----------
//   0        empty
//   1..18    increasing density: fourteen glyphs of a "Standard" ASCII-art ramp
//            (. - , : ; + = x X S # 0 8 @), plus four ANSI shade blocks (░ ▒ ▓ █)
//            for whatever needs more ink than a plain glyph can give — sorted by
//            MEASURED coverage at code-assignment time, not by this reading order
//   19..22   contour orientation: "-" "\" "|" "/"
//
// POURQUOI IL EXISTE. `ascii.ts` mesure honnêtement chaque glyphe (measure() rastérise, ne
// suppose rien) mais DIVISE ensuite toute l'échelle par la couverture du plus dense pour qu'il
// compte pour 1,0 — le même geste que `des` et `hachures` avant leurs versions pleines. Le
// commentaire du fichier le dit lui-même : « "@" only covers about half of its cell ». Mesuré
// ici (dans notre police, pas recopié d'un convertisseur calibré ailleurs) : 0,27, pas 0,5 —
// et donc « noir » y vaut 27 % d'encre, jamais plus.
//
// Deux leviers, mesurés sur la planche `essais/alphabets-tableaux/ascii.html` :
//   1. UNE VRAIE RAMPE. Quatorze glyphes plutôt que cinq — la rampe « Standard » d'un
//      convertisseur ASCII-art courant — tombent, dans IBM Plex Mono, sur une échelle quasi
//      parfaitement régulière (des pas de 0,015 à 0,020, contre un saut de 0,105 à 0,226 dans
//      la rampe actuelle). Ça ne change RIEN au plafond : mesuré seul, ce levier laisse
//      l'amplitude rendue identique à l'échelle littérale à cinq glyphes.
//   2. RELEVER LE PLAFOND, sans case pleine ni fond retourné : les quatre blocs de teinte ANSI
//      (░ ▒ ▓ █), pour tout ce qui a besoin de plus d'encre qu'un glyphe de texte n'en offre.
//      Seuls les trois derniers (▒ ▓ █) dépassent réellement « @ » — « ░ » tombe entre deux
//      glyphes de la rampe Standard en couverture, mais pas en texture (un grain pointillé,
//      pas une lettre), donc il reste dans la rampe plutôt que d'être écarté.
//
// L'échelle du tramage reste LITTÉRALE (pas de division par le plus sombre) : c'est le second
// levier qui compte, le premier n'a jamais suffi seul chez aucun des quatre alphabets touchés
// cette session (des, hachures, ascii, pointille).
//
// Identifiant neuf plutôt que `ascii` modifié en place : un vieux client face à un code
// au-delà de son tableau ferait `chars![code] === undefined`, donc une case vide — pas un
// plantage (contrairement à des avant desPlein), mais faux tout de même. Le filtre de
// catalogue par alphabets déclarés le rend impossible.

import { inkColor } from '../ink';
import type { Alphabet } from './types';

/** La rampe « Standard », dans son ordre de lecture habituel (clair -> sombre) — l'ORDRE DE
 *  CODE final vient du tri par couverture mesurée dans measure(), pas de celui-ci : « ░ »,
 *  ajouté après, ne serait pas à sa place si on se contentait de le mettre en dernier. */
const RAMP = [
  '.',
  '-',
  ',',
  ':',
  ';',
  '+',
  '=',
  'x',
  'X',
  'S',
  '#',
  '0',
  '8',
  '@',
  '░',
  '▒',
  '▓',
  '█',
];
const DIRS = ['-', '\\', '|', '/'];
const FONT = '"IBM Plex Mono", ui-monospace, monospace';

/** Nombre de paliers de densité (18) et de paliers protégés de la substitution de contour en
 *  fin de rampe (3) — après tri par couverture, ce sont les trois vrais plus sombres (▒ ▓ █) ;
 *  « ░ », plus clair que « @ », reste éligible comme n'importe quel ton moyen. */
const DENSITY_COUNT = RAMP.length;
const PURE_TAIL = 3;

let chars: string[] | null = null; // code 0..N -> character
let steps: number[] | null = null; // literal ink coverage of each density level
let aspect = 0.6; // width/height of a cell = glyph advance / em

/**
 * La boîte d'ENCRE d'un glyphe, en em, autour du point où fillText le pose (donc avec
 * `textAlign: center` et `textBaseline: middle`, les réglages du dessin) : de combien l'encre
 * monte, descend, part à gauche et à droite de ce point.
 *
 * Elle existe parce que la boîte d'encre n'est PAS la case. Une police pose ses glyphes dans un
 * cadre plus haut que l'em — les quatre blocs de teinte (░ ▒ ▓ █) sont faits pour carreler une
 * ligne de texte, pas un carré — et « middle » n'est pas non plus le milieu de l'encre. Dessiné
 * à une police égale à la hauteur de case, un bloc dépassait donc de 7 à 8 px au-dessus d'une
 * case de 40 (mesuré : essais/verif-asciielargi-case.html), c'est-à-dire d'un cinquième de case
 * SUR SA VOISINE DU DESSUS. Mesurée, la boîte permet de poser chaque glyphe dans sa case.
 */
type InkBox = { asc: number; desc: number; left: number; right: number };
let boxes: InkBox[] | null = null; // code 0..N -> boîte d'encre

/** Les quatre blocs de teinte ANSI. Ils reçoivent un traitement à part dans draw() : ce sont
 *  des PAVÉS de texture et non des lettres, faits pour couvrir sans laisser de joint, donc on
 *  les étire sur la case au lieu de les poser comme un caractère. */
const BLOCKS = new Set(['░', '▒', '▓', '█']);

/** Measures the actual coverage of each glyph by rasterizing it: the ramp is only monotonic
 *  by accident, guessing it breaks the midtones — and the blocks have no geometry to guess
 *  from in the first place. */
export function measure(): void {
  const S = 48;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const x = c.getContext('2d')!;
  x.font = `${S}px ${FONT}`;
  aspect = x.measureText('M').width / S || 0.6;

  const cover = (ch: string) => {
    x.clearRect(0, 0, S, S);
    x.font = `${S}px ${FONT}`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillStyle = '#000';
    x.fillText(ch, S / 2, S / 2);
    const d = x.getImageData(0, 0, S, S).data;
    let a = 0;
    for (let i = 3; i < d.length; i += 4) a += d[i];
    return a / (255 * S * S * aspect); // relative to the glyph's box, not the canvas
  };

  const ramp = RAMP.map((ch) => ({ ch, k: cover(ch) })).sort((a, b) => a.k - b.k);
  chars = [' ', ...ramp.map((r) => r.ch), ...DIRS];
  // Littérale, à la différence de ascii.ts : diviser par le plus sombre est très exactement
  // ce qui rend l'alphabet actuel délavé (cf. l'en-tête).
  steps = [0, ...ramp.map((r) => r.k)];

  // Les boîtes d'encre, dans les MÊMES réglages que draw() : les quatre nombres se comptent
  // depuis le point passé à fillText, donc ils n'ont de sens qu'avec ce centrage-là.
  x.font = `${S}px ${FONT}`;
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  boxes = chars.map((ch) => {
    if (!ch || ch === ' ') return { asc: 0, desc: 0, left: 0, right: 0 };
    const m = x.measureText(ch);
    return {
      asc: m.actualBoundingBoxAscent / S,
      desc: m.actualBoundingBoxDescent / S,
      left: m.actualBoundingBoxLeft / S,
      right: m.actualBoundingBoxRight / S,
    };
  });
}

function ready(): void {
  if (!chars) measure();
}

/** The measured ramp, for asciiElargi.encode.ts. */
export function asciiElargiSteps(): number[] {
  ready();
  return steps!;
}

export const asciiElargi: Alphabet = {
  id: 'asciiElargi',
  label: 'ASCII élargi',
  symbolName: 'caractère',
  helpText:
    'Alphabet ASCII, en dix-huit paliers de <b>densité</b> plutôt que cinq : quatorze ' +
    'caractères pour les demi-teintes, quatre blocs de teinte pour les ombres profondes. Sur ' +
    'les tons moyens, le glyphe <b>suit le contour</b> (<code>- \\ | /</code>).',

  aspect() {
    ready();
    return aspect;
  },

  // Les quinze plus clairs, puis les quatre contours, puis les trois vrais plus sombres —
  // même logique que ascii.ts : le plus dense ferme la rampe plutôt que de l'ouvrir.
  paletteOrder: [
    ...Array.from({ length: DENSITY_COUNT - PURE_TAIL }, (_, i) => i + 1),
    ...Array.from({ length: DIRS.length }, (_, i) => DENSITY_COUNT + 1 + i),
    ...Array.from({ length: PURE_TAIL }, (_, i) => DENSITY_COUNT - PURE_TAIL + 1 + i),
  ],

  remeasure: measure,

  codeName: (code) => {
    ready();
    const ch = chars![code];
    return code > DENSITY_COUNT ? `contour « ${ch} »` : `densité « ${ch} »`;
  },

  /**
   * Le glyphe, DANS sa case — l'encre y tient, entièrement, et le mot compte : une case remplie
   * ne doit rien poser sur sa voisine, sans quoi la feuille se salit à mesure qu'on la remplit
   * et le joueur voit le débordement avant de voir le dessin.
   *
   * Deux traitements, parce qu'il y a deux natures de signe dans cet alphabet :
   *
   *   - un PAVÉ de teinte (░ ▒ ▓ █) est étiré sur la case, en largeur comme en hauteur. Ce
   *     n'est pas une lettre, c'est une texture faite pour carreler, et une mise à l'échelle
   *     uniforme ne peut pas la faire tenir : sa boîte d'encre est plus haute que large par
   *     rapport à la case (elle carrelle une LIGNE de texte, plus haute que l'em), donc la
   *     rétrécir pour qu'elle rentre en hauteur lui ferait perdre la largeur — un joint de
   *     papier nu entre deux cases noires voisines, lu comme des bandes pâles. C'est
   *     exactement le défaut que render() décrit et refuse ailleurs.
   *
   *   - une LETTRE garde sa taille et SA PLACE naturelles, à un rattrapage près. Sa taille,
   *     parce que c'est la couverture relative des glyphes qui fait les demi-teintes : grossir
   *     un « . » pour qu'il remplisse sa case reviendrait à mentir sur le ton. Sa place, parce
   *     que la hauteur d'un caractère dans sa ligne est une information de dessin — une virgule
   *     est basse, un apostrophe est haut, un tiret est au milieu, et c'est ce qui fait
   *     ressembler la feuille à de l'ASCII art. On ne la RECENTRE donc pas sur son encre : on
   *     la repousse seulement de ce qu'il faut, et seulement quand son encre sortirait de la
   *     case — un pixel pour le « S », le « 0 », le « 8 » et le « | », qui mordaient sur la
   *     case du dessus.
   */
  draw(ctx, x, y, w, h, code) {
    ready();
    const ch = chars![code];
    if (!ch || ch === ' ') return;
    const b = boxes![code];
    ctx.fillStyle = inkColor();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (BLOCKS.has(ch)) {
      const iw = b.left + b.right;
      const ih = b.asc + b.desc;
      if (iw <= 0 || ih <= 0) return;
      // La police est posée AVANT la transformation : c'est la matrice qui met la boîte d'encre
      // à la taille de la case, et le rastériseur voit donc la bonne taille finale.
      ctx.font = `${h}px ${FONT}`;
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(w / (iw * h), h / (ih * h));
      ctx.fillText(ch, b.left * h, b.asc * h);
      ctx.restore();
      return;
    }

    // La boîte annoncée par la police est la boîte de MISE EN PAGE ; le rastériseur, lui,
    // étale son anticrénelage un demi-pixel au-delà. On rentre donc d'un cheveu de plus que le
    // strict nécessaire — 2 % de la case, invisible à l'œil — sinon un « S » calé pile sur le
    // bord laisse quand même une frange sur la case du dessus.
    const pad = h * 0.02;
    const avail = h - 2 * pad;
    const ink = b.asc + b.desc;
    // em = hauteur de case, sauf pour les glyphes plus hauts qu'elle (« | » couvre toute la
    // ligne, donc plus d'un em) : ceux-là descendent à la taille qui rentre.
    const f = ink * h > avail ? avail / ink : h;
    ctx.font = `${f}px ${FONT}`;
    // Le placement de « middle », puis le minimum pour rentrer. Après le rétrécissement
    // ci-dessus l'encre fait au plus la hauteur de la case, donc un seul des deux côtés peut
    // dépasser et le rattrapage ne se contredit jamais.
    let oy = y + h / 2;
    const top = oy - b.asc * f;
    const bottom = oy + b.desc * f;
    if (top < y + pad) oy += y + pad - top;
    else if (bottom > y + h - pad) oy -= bottom - (y + h - pad);
    ctx.fillText(ch, x + w / 2, oy);
  },

  /** Une case au rapport de l'alphabet, centrée dans la pastille, plutôt que la pastille
   *  entière : la marge du haut et du bas est là parce qu'un « | » remplirait sinon toute la
   *  hauteur, et la LARGEUR compte depuis que les pavés de teinte s'étirent sur leur case —
   *  étalés sur une pastille plus large que la case, ils seraient les seuls signes de la
   *  palette à ne pas avoir la forme qu'ils prendront sur la feuille. */
  swatch(ctx, w, h, code) {
    ready();
    const bh = h - 6;
    const bw = bh * aspect;
    asciiElargi.draw(ctx, (w - bw) / 2, 3, bw, bh, code);
  },
};

export { DENSITY_COUNT, PURE_TAIL };
